/**
 * Giro3DToolbar — slim in-canvas overlay (WP-6 §8.6).
 *
 * Hosts only what the right-rail `<ThreeDModelToolCard />` and
 * `<SectionAndClipToolCard />` cannot: a vertex-snap cursor indicator,
 * raycaster-driven measurement modes, and a `<PointCloudQualityPanel />`
 * embed when point clouds are loaded. The Three.js measurement
 * primitives live in `./measureController.ts`; the React state machine
 * lives in `./useMeasureTool.ts`. Public API is unchanged.
 */

import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, IconButton, tokens } from '@/design';
import type { Giro3DAdapter, PointCloudQualitySnapshot } from './Giro3DAdapter';
import type { UnifiedSceneTreeNode } from '@/app/scenePackage';
import { PointCloudQualityPanel } from '@/components/pointcloud/PointCloudQualityPanel';
import { TileLoadChip } from '@/components/three-d/TileLoadChip';
import type { MeasureSurfaces } from './measureController';
import { useMeasureTool, type MeasureMode } from './useMeasureTool';
import type { ScaleMode } from '@/crs/crsAligner';

type ActivePanel = 'none' | 'pointcloudQuality';

export interface Giro3DToolbarProps {
  adapter: Giro3DAdapter | null;
  sceneTree?: UnifiedSceneTreeNode;
  selectedSceneNode?: UnifiedSceneTreeNode | null;
  onSceneNodeSelected?: (node: UnifiedSceneTreeNode) => void;
}

export function Giro3DToolbar({ adapter }: Giro3DToolbarProps) {
  const [activePanel, setActivePanel] = useState<ActivePanel>('none');
  const [pcq, setPcq] = useState<PointCloudQualitySnapshot | null>(null);
  const [displayMode, setDisplayMode] = useState<ScaleMode>('visual');
  const [showMeasurementNote, setShowMeasurementNote] = useState(false);

  const getSurfaces = useCallback((): MeasureSurfaces | null => {
    const inst = adapter?.getInstance() as any;
    if (!inst?.domElement) return null;
    return {
      scene: inst.scene,
      camera: inst.view?.camera,
      domEl: inst.domElement,
      notifyChange: () => inst.notifyChange?.(),
    };
  }, [adapter]);

  const measure = useMeasureTool(getSurfaces);

  const refreshPCQ = useCallback(() => {
    setPcq(adapter?.getPointCloudQuality?.() ?? null);
  }, [adapter]);

  useEffect(() => {
    refreshPCQ();
    const handle = window.setInterval(refreshPCQ, 1500);
    return () => window.clearInterval(handle);
  }, [refreshPCQ]);

  const setQuality = useCallback(
    (next: Parameters<Giro3DAdapter['setPointCloudQuality']>[0]) => {
      adapter?.setPointCloudQuality(next);
      refreshPCQ();
    },
    [adapter, refreshPCQ],
  );

  const handleDisplayModeChange = useCallback(
    async (mode: ScaleMode) => {
      if (!adapter) return;
      try {
        await adapter.setDefaultScaleMode(mode);
        setDisplayMode(mode);
        setShowMeasurementNote(true);
      } catch (err) {
        console.warn('[Giro3DToolbar] Failed to set scale mode:', err);
      }
    },
    [adapter],
  );

  if (!adapter) return null;

  return (
    <div style={dockStyle}>
      <TileLoadChip adapter={adapter} />

      {measure.cursorSnap ? (
        <span aria-hidden style={snapDotStyle(measure.cursorSnap)} />
      ) : null}

      <div style={panelStyle} role="toolbar" aria-label="Giro3D measurement tools">
        <Section label="Measure">
          <ToolBtn
            label="Dist"
            mode="distance"
            active={measure.mode}
            onClick={measure.start}
            title="Distance: click 2 points (snaps to vertex)"
          />
          <ToolBtn
            label="Ht"
            mode="height"
            active={measure.mode}
            onClick={measure.start}
            title="Height: vertical distance between 2 points"
          />
          <ToolBtn
            label="Pt"
            mode="point"
            active={measure.mode}
            onClick={measure.start}
            title="Coordinate: click to get XYZ"
          />
          {measure.results.length > 0 ? (
            <button
              type="button"
              onClick={measure.clear}
              title="Clear all measurements"
              aria-label="Clear all measurements"
              style={dangerBtnStyle}
            >
              Clear
            </button>
          ) : null}
        </Section>

        <Section label="Display">
          <button
            type="button"
            data-testid="display-mode-visual"
            onClick={() => handleDisplayModeChange('visual')}
            aria-pressed={displayMode === 'visual'}
            aria-label="Visual display mode"
            style={{
              ...btnStyle(displayMode === 'visual'),
              marginRight: tokens.space[1],
            }}
            title="Visual mode: maintains web map scaling (measurements may be distorted)"
          >
            Visual
          </button>
          <button
            type="button"
            data-testid="display-mode-engineering"
            onClick={() => handleDisplayModeChange('engineering')}
            aria-pressed={displayMode === 'engineering'}
            aria-label="Engineering display mode"
            style={btnStyle(displayMode === 'engineering')}
            title="Engineering mode: true 1:1 meter relationships (visual scaling may appear inconsistent)"
          >
            Engineering
          </button>
        </Section>

        {showMeasurementNote ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
            background: tokens.color.surface[2],
            borderRadius: tokens.radius.md,
            fontSize: tokens.font.size.xs,
            color: tokens.color.ink.secondary,
          }}>
            <span>Measurements always report true meters regardless of display mode.</span>
            <button
              type="button"
              data-testid="dismiss-measurement-note"
              onClick={() => setShowMeasurementNote(false)}
              style={{
                background: 'none',
                border: 'none',
                color: tokens.color.ink.muted,
                cursor: 'pointer',
                padding: 0,
              }}
              aria-label="Dismiss measurement note"
            >
              ×
            </button>
          </div>
        ) : null}

        {measure.mode !== 'none' ? (
          <p style={hintStyle}>
            {measure.mode === 'point'
              ? 'Click a point on the model'
              : measure.pointsCollected === 0
                ? 'Click first point'
                : 'Click second point'}
          </p>
        ) : null}

        {pcq ? (
          <Section label="Point cloud">
            <button
              type="button"
              aria-pressed={activePanel === 'pointcloudQuality'}
              aria-label="Point cloud quality settings"
              title="Point-cloud quality, LOD, occlusion, and coloring"
              onClick={() =>
                setActivePanel(activePanel === 'pointcloudQuality' ? 'none' : 'pointcloudQuality')
              }
              style={btnStyle(activePanel === 'pointcloudQuality')}
            >
              Quality
            </button>
          </Section>
        ) : null}
      </div>

      {measure.results.length > 0 ? (
        <div style={panelStyle}>
          <p style={titleStyle}>Measurements</p>
          <ul style={listStyle}>
            {measure.results.map((m, i) => (
              <li key={`${m.type}-${i}`} style={itemStyle}>
                <span style={typeStyle}>{m.type}</span>
                <span style={valueStyle}>{m.value}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {activePanel === 'pointcloudQuality' && pcq ? (
        <div style={panelStyle}>
          <header style={headerStyle}>
            <span style={titleStyle}>Point Cloud Quality</span>
            <IconButton
              type="button"
              aria-label="Close point cloud quality panel"
              onClick={() => setActivePanel('none')}
              dense
              style={{ color: tokens.color.ink.muted }}
            >
              <Icon name="close" size={20} decorative />
            </IconButton>
          </header>
          <PointCloudQualityPanel quality={pcq} onChange={setQuality} embedded />
        </div>
      ) : null}
    </div>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div style={sectionLabelStyle}>{label}</div>
      <div style={rowStyle}>{children}</div>
    </div>
  );
}

function ToolBtn({
  label, mode, active, onClick, title,
}: {
  label: string;
  mode: MeasureMode;
  active: MeasureMode;
  onClick: (mode: MeasureMode) => void;
  title?: string;
}) {
  const isActive = active === mode;
  return (
    <button
      type="button"
      onClick={() => onClick(mode)}
      title={title ?? label}
      aria-label={title ?? `Measure ${label}`}
      aria-pressed={isActive}
      style={btnStyle(isActive)}
    >
      {label}
    </button>
  );
}

function snapDotStyle(c: { x: number; y: number }): CSSProperties {
  return {
    position: 'fixed',
    left: c.x + tokens.space[3],
    top: c.y - tokens.space[3],
    width: tokens.space[2],
    height: tokens.space[2],
    borderRadius: tokens.radius.pill,
    background: tokens.color.semantic.success,
    border: tokens.border.thin,
    pointerEvents: 'none',
    zIndex: tokens.zIndex.tooltip,
  };
}

function btnStyle(active: boolean): CSSProperties {
  return {
    minHeight: tokens.density.touchMin,
    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: active ? tokens.color.accent.primary : tokens.color.surface[0],
    color: active ? tokens.color.accent.on : tokens.color.ink.primary,
    cursor: 'pointer',
    fontSize: tokens.font.size.sm,
    fontWeight: active ? tokens.font.weight.medium : tokens.font.weight.regular,
    lineHeight: tokens.font.lineHeight.normal,
    whiteSpace: 'nowrap',
  };
}

const dangerBtnStyle: CSSProperties = {
  minHeight: tokens.density.touchMin,
  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
  border: tokens.border.thin,
  borderRadius: tokens.radius.input,
  background: 'transparent',
  color: tokens.color.semantic.danger,
  cursor: 'pointer',
  fontSize: tokens.font.size.sm,
  whiteSpace: 'nowrap',
};

const dockStyle: CSSProperties = {
  position: 'absolute',
  top: tokens.space[3],
  right: tokens.space[3],
  zIndex: tokens.zIndex.panel,
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.space[2],
  maxHeight: 'calc(100vh - 80px)',
};

const panelStyle: CSSProperties = {
  background: tokens.color.surface[1],
  border: tokens.border.thin,
  borderRadius: tokens.radius.md,
  padding: tokens.space[2],
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.space[1],
};

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: tokens.space[2],
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontWeight: tokens.font.weight.medium,
  color: tokens.color.ink.primary,
};

const sectionLabelStyle: CSSProperties = {
  fontSize: tokens.font.size.xs,
  textTransform: 'uppercase',
  letterSpacing: tokens.border.width.thin,
  color: tokens.color.ink.muted,
  marginBottom: tokens.space[1],
};

const rowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: tokens.space[1],
};

const hintStyle: CSSProperties = {
  margin: 0,
  fontSize: tokens.font.size.xs,
  color: tokens.color.accent.primary,
  padding: tokens.space[1],
};

const listStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.space[1],
};

const itemStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: tokens.space[2],
  color: tokens.color.ink.secondary,
  fontSize: tokens.font.size.xs,
};

const typeStyle: CSSProperties = {
  textTransform: 'capitalize',
  color: tokens.color.accent.primary,
};

const valueStyle: CSSProperties = { fontFamily: tokens.font.family.mono };
